// CSV Export Utility Functions
// Provides functionality to export data to CSV format

/**
 * Converts an array of objects to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions with {key, label} structure
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row
  const headers = columns.map(col => `"${col.label}"`).join(',');
  
  // Create data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.key];
      
      // Handle nested objects (e.g., category.name)
      if (col.key.includes('.')) {
        const keys = col.key.split('.');
        value = keys.reduce((obj, key) => obj?.[key], item);
      }
      
      // Handle arrays (e.g., tags, pros, cons, etc.)
      if (Array.isArray(value)) {
        value = value.map(v => {
          if (typeof v === 'object' && v !== null) {
            // Handle keyIngredients with name and benefit
            if (v.name && v.benefit) {
              return `${v.name}: ${v.benefit}`;
            }
            // Handle other objects with name property
            return v.name || JSON.stringify(v);
          }
          return String(v);
        }).join('; ');
      }
      
      // Handle dates
      if (value instanceof Date) {
        value = value.toISOString().split('T')[0];
      }
      
      // Handle boolean values
      if (typeof value === 'boolean') {
        value = value ? 'Yes' : 'No';
      }
      
      // Convert to string and escape quotes
      value = String(value || '');
      value = value.replace(/"/g, '""');
      
      return `"${value}"`;
    }).join(',');
  });
  
  return [headers, ...rows].join('\n');
};

/**
 * Downloads CSV data as a file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Exports data to CSV with proper formatting
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Name of the CSV file
 */
export const exportToCSV = (data, columns, filename) => {
  const csvContent = convertToCSV(data, columns);
  downloadCSV(csvContent, filename);
};

// Predefined column configurations for different data types

export const categoryColumns = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'isActive', label: 'Status' },
  { key: 'imageUrl', label: 'Image URL' },
  { key: 'createdAt', label: 'Created Date' },
  { key: 'updatedAt', label: 'Updated Date' }
];

export const subcategoryColumns = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'category.name', label: 'Category' },
  { key: 'isActive', label: 'Status' },
  { key: 'imageUrl', label: 'Image URL' },
  { key: 'createdAt', label: 'Created Date' },
  { key: 'updatedAt', label: 'Updated Date' }
];

export const tagColumns = [
  { key: 'name', label: 'Name' },
  { key: 'category.name', label: 'Category' },
  { key: 'subcategory.name', label: 'Subcategory' },
  { key: 'isActive', label: 'Status' },
  { key: 'createdAt', label: 'Created Date' },
  { key: 'updatedAt', label: 'Updated Date' }
];

// Comprehensive product columns including all expandable features
// This includes all fields from the Product model including:
// - Basic product info (name, brand, price, etc.)
// - Expandable features (pros, cons, key ingredients, etc.)
// - Product attributes (fragrance, size, suitability, etc.)
// - Benefits and highlights
// - Categorization and metadata
export const productColumns = [
  // Basic Information
  { key: 'name', label: 'Product Name' },
  { key: 'brand', label: 'Brand' },
  { key: 'description', label: 'Description' },
  { key: 'shortDescription', label: 'Short Description' },
  { key: 'price', label: 'Price' },
  { key: 'currency', label: 'Currency' },
  { key: 'discount', label: 'Discount (%)' },
  { key: 'rating', label: 'Rating' },
  { key: 'reviewCount', label: 'Review Count' },
  
  // Categorization
  { key: 'category.name', label: 'Category' },
  { key: 'subcategory.name', label: 'Subcategory' },
  { key: 'tags', label: 'Tags' },
  
  // Status & Features
  { key: 'isActive', label: 'Status' },
  { key: 'isFeatured', label: 'Featured' },
  { key: 'badges', label: 'Badges' },
  
  // Links & Media
  { key: 'imageUrl', label: 'Image URL' },
  { key: 'affiliateLink', label: 'Affiliate Link' },
  { key: 'slug', label: 'Slug' },
  
  // Expandable Features - Pros & Cons
  { key: 'pros', label: 'Pros' },
  { key: 'cons', label: 'Cons' },
  
  // Key Ingredients (formatted as "Name: Benefit")
  { key: 'keyIngredients', label: 'Key Ingredients' },
  
  // Product Attributes
  { key: 'suitability', label: 'Suitability' },
  { key: 'attributes', label: 'Attributes' },
  { key: 'fragrance', label: 'Fragrance' },
  { key: 'size', label: 'Size' },
  
  // Benefits & Highlights
  { key: 'highlights', label: 'Highlights' },
  { key: 'benefits', label: 'Benefits' },
  { key: 'radianceTake', label: 'Radiance Take' },
  
  // Metadata
  { key: 'createdAt', label: 'Created Date' },
  { key: 'updatedAt', label: 'Updated Date' }
];
